import React, { useEffect, useState } from 'react';
import { Instagram, Linkedin, Menu, X, ChevronUp } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { usePageTransition } from './PageTransition';

// Logo images
const logoWhite = '/assets/domus-logo-white.png';
const logoBlack = '/assets/domus-logo-black.png';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const location = useLocation();
  const { navigateWithTransition } = usePageTransition();

  // Determine if we're on a separate page (not homepage)
  const isSubPage = location.pathname !== '/';
  
  // Check if on maintenance page
  const isMaintenancePage = ['/projects', '/gallery', '/studio'].includes(location.pathname) || 
                            location.pathname.startsWith('/projects/') || 
                            location.pathname.startsWith('/gallery/');
  
  // Pages with dark backgrounds need white text
  // All subpages have dark backgrounds now
  const isDarkBackground = ['/studio', '/contact', '/projects', '/gallery'].includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      // At the very top of the page - show floating header, hide dropdown
      if (scrollPosition < 60) {
        setIsVisible(true);
        setIsScrolled(false);
      } 
      // Scrolled past threshold - hide floating header, show dropdown
      else {
        setIsVisible(false);
        setIsScrolled(true);
      }
      
      setShowBackToTop(scrollPosition > window.innerHeight);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavClick = (e, to) => {
    e.preventDefault();
    navigateWithTransition(to);
  };

  const handleMenuNavClick = (e, to) => {
    e.preventDefault();
    setMenuOpen(false);
    navigateWithTransition(to);
  };

  // Subpage header - Logo on left, hamburger on right (both desktop and mobile)
  if (isSubPage) {
    const headerColorClass = isDarkBackground ? 'subpage-header-light' : 'subpage-header-dark';
    const subpageLogo = isDarkBackground ? logoWhite : logoBlack;
    
    return (
      <>
        {/* Floating header: Domus Living on left, hamburger on right - fades out on scroll */}
        <header className={`subpage-simple-header ${headerColorClass} ${!isVisible ? 'header-fading' : ''}`}>
          <a href="/" className="subpage-brand-left" onClick={(e) => handleNavClick(e, '/')}>
            <img src={subpageLogo} alt="Domus Living" className="brand-logo-subpage" />
          </a>
          <button 
            className="subpage-hamburger-btn"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>
        </header>

        {/* Solid dropdown header - drops in after floating header fades */}
        <header className={`top-bar subpage-top-bar ${isScrolled ? 'top-bar-visible' : ''}`}>
          <a href="/" className="top-bar-brand" onClick={(e) => handleNavClick(e, '/')}>
            <img src={logoBlack} alt="Domus Living" className="brand-logo-topbar" />
          </a>
          <button 
            className="hamburger-menu"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>
        </header>

        {/* Back to Top Button */}
        <button 
          className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Back to top"
        >
          <ChevronUp size={28} />
        </button>

        {/* Full Screen Menu */}
        <div className={`fullscreen-menu ${menuOpen ? 'menu-open' : ''}`}>
          <button 
            className="menu-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <X size={32} />
          </button>
          <nav className="fullscreen-nav">
            <a href="/" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/')}>Home</a>
            <a href="/projects" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/projects')}>Projects</a>
            <a href="/studio" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/studio')}>Studio</a>
            <a href="/gallery" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/gallery')}>Gallery</a>
            <a href="/contact" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/contact')}>Contact</a>
          </nav>
          <div className="fullscreen-footer">
            <div className="fullscreen-social">
              <a href="https://instagram.com/domus.living" target="_blank" rel="noopener noreferrer" className="fullscreen-social-icon" aria-label="Instagram">
                <Instagram size={16} />
              </a>
              <a href="https://www.linkedin.com/company/domus-living" target="_blank" rel="noopener noreferrer" className="fullscreen-social-icon" aria-label="LinkedIn">
                <Linkedin size={16} />
              </a>
            </div>
            <img src={logoWhite} alt="Domus Living" className="brand-logo-menu" />
          </div>
        </div>
      </>
    );
  }

  // Full header for homepage - centered nav with social icons on right
  return (
    <>
      {/* Desktop Header - Centered nav links with social icons on right */}
      <header className={`main-header ${!isVisible ? 'header-hidden' : ''}`}>
        <nav className="header-nav header-nav-centered">
          <div className="nav-center">
            <a href="/projects" className="nav-link" onClick={(e) => handleNavClick(e, '/projects')}>Projects</a>
            <a href="/studio" className="nav-link" onClick={(e) => handleNavClick(e, '/studio')}>Studio</a>
            <a href="/gallery" className="nav-link" onClick={(e) => handleNavClick(e, '/gallery')}>Gallery</a>
            <a href="/contact" className="nav-link" onClick={(e) => handleNavClick(e, '/contact')}>Contact</a>
          </div>
          <div className="nav-social">
            <a href="https://instagram.com/domus.living" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
              <Instagram size={15} />
            </a>
            <a href="https://www.linkedin.com/company/domus-living" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="LinkedIn">
              <Linkedin size={15} />
            </a>
          </div>
        </nav>
      </header>

      {/* Mobile Floating Header - just hamburger on right (visible on mobile only) */}
      <header className={`homepage-mobile-header ${!isVisible ? 'header-fading' : ''}`}>
        <div className="homepage-mobile-spacer"></div>
        <button 
          className="subpage-hamburger-btn subpage-header-light"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <Menu size={24} />
        </button>
      </header>

      {/* Dropdown Header - Shows on scroll (both desktop and mobile) */}
      <header className={`top-bar subpage-top-bar ${isScrolled ? 'top-bar-visible' : ''}`}>
        <a href="/" className="top-bar-brand" onClick={(e) => { e.preventDefault(); scrollToTop(); }}>
          <img src={logoBlack} alt="Domus Living" className="brand-logo-topbar" />
        </a>
        <button 
          className="hamburger-menu"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <Menu size={24} />
        </button>
      </header>

      {/* Back to Top Button */}
      <button 
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ChevronUp size={28} />
      </button>

      {/* Full Screen Menu */}
      <div className={`fullscreen-menu ${menuOpen ? 'menu-open' : ''}`}>
        <button 
          className="menu-close"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          <X size={32} />
        </button>
        <nav className="fullscreen-nav">
          <a href="/" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/')}>Home</a>
          <a href="/projects" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/projects')}>Projects</a>
          <a href="/studio" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/studio')}>Studio</a>
          <a href="/gallery" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/gallery')}>Gallery</a>
          <a href="/contact" className="fullscreen-link" onClick={(e) => handleMenuNavClick(e, '/contact')}>Contact</a>
        </nav>
        <div className="fullscreen-footer">
          <div className="fullscreen-social">
            <a href="https://instagram.com/domus.living" target="_blank" rel="noopener noreferrer" className="fullscreen-social-icon" aria-label="Instagram">
              <Instagram size={16} />
            </a>
            <a href="https://www.linkedin.com/company/domus-living" target="_blank" rel="noopener noreferrer" className="fullscreen-social-icon" aria-label="LinkedIn">
              <Linkedin size={16} />
            </a>
          </div>
          <img src={logoWhite} alt="Domus Living" className="brand-logo-menu" />
        </div>
      </div>
    </>
  );
};
